import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner, Card } from 'react-bootstrap';
import { FaArrowRight, FaArrowLeft, FaGripVertical, FaEye, FaFilter } from 'react-icons/fa';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import SearchViewApi from '../../../api/SearchViewApi';
import PubSub from 'pubsub-js';

const SearchViewDialog = ({ show, onClose, objectName: initialObjectName, searchViewData }) => {
  const [objectName, setObjectName] = useState(initialObjectName || '');
  const [objects, setObjects] = useState([]);

  // Display Fields
  const [displayFields, setDisplayFields] = useState([]);
  const [availableDisplayFields, setAvailableDisplayFields] = useState([]);

  // Filterable Fields
  const [filterableFields, setFilterableFields] = useState([]);
  const [availableFilterableFields, setAvailableFilterableFields] = useState([]);

  const [allFields, setAllFields] = useState([]);

  // Drag and drop states for display fields
  const [draggedDisplayItem, setDraggedDisplayItem] = useState(null);
  const [dragOverDisplayItem, setDragOverDisplayItem] = useState(null);

  // Drag and drop states for filterable fields
  const [draggedFilterableItem, setDraggedFilterableItem] = useState(null);
  const [dragOverFilterableItem, setDragOverFilterableItem] = useState(null);

  const [sortBy, setSortBy] = useState('createddate');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [viewLabel, setViewLabel] = useState('');
  const [searchViewName, setSearchViewName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [displayFieldSearchTerm, setDisplayFieldSearchTerm] = useState('');
  const [filterableFieldSearchTerm, setFilterableFieldSearchTerm] = useState('');

  const editMode = !!searchViewData;

  useEffect(() => {
    if (show) {
      fetchObjects();

      // If edit mode, set objectName from searchViewData first
      if (editMode && searchViewData && searchViewData.object_name) {
        if (objectName !== searchViewData.object_name) {
          setObjectName(searchViewData.object_name);
        }
      } else if (!editMode && initialObjectName && objectName !== initialObjectName) {
        setObjectName(initialObjectName);
      }
    } else {
      // Reset state when dialog closes
      setObjectName(initialObjectName || '');
      setDisplayFields([]);
      setFilterableFields([]);
      setAvailableDisplayFields([]);
      setAvailableFilterableFields([]);
      setAllFields([]);
      setSortBy('createddate');
      setSortOrder('DESC');
      setViewLabel('');
      setSearchViewName('');
      setError('');
      setDisplayFieldSearchTerm('');
      setFilterableFieldSearchTerm('');
    }
  }, [show, editMode, searchViewData, initialObjectName]);

  // Fetch fields when objectName is available
  useEffect(() => {
    if (show && objectName) {
      fetchFields();
    }
  }, [show, objectName]);

  const fetchObjects = async () => {
    try {
      const response = await ObjectConfigApi.fetchObjectConfigs();
      if (response?.success) {
        const objectList = response.data
          .filter(obj => !obj.is_deleted)
          .map(obj => ({
            name: obj.object_name,
            label: obj.object_label || obj.object_name
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setObjects(objectList);
      }
    } catch (err) {
      console.error('Error fetching objects:', err);
      setError('Failed to fetch objects');
    }
  };

  const fetchFields = async () => {
    if (!objectName) return;

    setLoading(true);
    setError('');
    try {
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
      const result = response.data;

      if (result?.fields?.length > 0) {
        const fields = result.fields.map((f) => ({
          id: f.id,
          label: f.field_label,
          name: f.field_name,
          dataType: f.data_type || 'text'
        }));

        setAllFields(fields);

        if (editMode && searchViewData) {
          // Edit mode: load existing data after fields are fetched
          loadExistingData(fields);
        } else {
          // Create mode: default to ID field in display fields
          const idField = fields.find(f => f.name === 'id');
          if (idField) {
            setDisplayFields([idField]);
            setAvailableDisplayFields(fields.filter(f => f.name !== 'id'));
            setFilterableFields([]);
            setAvailableFilterableFields(fields);
          } else {
            setDisplayFields([]);
            setAvailableDisplayFields(fields);
            setFilterableFields([]);
            setAvailableFilterableFields(fields);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching fields:', err);
      setError('Failed to load fields');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingData = (fieldsToUse = null) => {
    if (!searchViewData) return;

    const fields = fieldsToUse || allFields;
    if (fields.length === 0) return;

    const displayFieldNames = searchViewData.display_fields || [];
    const filterableFieldNames = searchViewData.filterable_fields || [];

    // Preserve the order from saved data by mapping in the order they appear in the saved arrays
    const display = displayFieldNames
      .map(fieldName => fields.find(f => f.name === fieldName))
      .filter(Boolean); // Remove any undefined fields

    const filterable = filterableFieldNames
      .map(fieldName => fields.find(f => f.name === fieldName))
      .filter(Boolean); // Remove any undefined fields

    // Available fields are those not in the respective list
    const availableDisplay = fields.filter(f => !displayFieldNames.includes(f.name));
    const availableFilterable = fields.filter(f => !filterableFieldNames.includes(f.name));

    setDisplayFields(display);
    setFilterableFields(filterable);
    setAvailableDisplayFields(availableDisplay);
    setAvailableFilterableFields(availableFilterable);
    setSortBy(searchViewData.sort_by || 'createddate');
    setSortOrder(searchViewData.sort_order || 'DESC');
    setViewLabel(searchViewData.view_label || '');
    setSearchViewName(searchViewData.search_view_name || '');
  };

  const handleObjectChange = (e) => {
    const newObjectName = e.target.value;
    setObjectName(newObjectName);
    setViewLabel('');
    setSearchViewName('');
    setDisplayFields([]);
    setFilterableFields([]);
    setAvailableDisplayFields([]);
    setAvailableFilterableFields([]);
    setAllFields([]);
    if (newObjectName) {
      fetchFields();
    }
  };

  // Display Fields Functions
  const moveToDisplay = (field) => {
    setDisplayFields([...displayFields, field]);
    setAvailableDisplayFields(availableDisplayFields.filter(f => f.id !== field.id));
  };

  const moveFromDisplay = (field) => {
    setDisplayFields(displayFields.filter(f => f.id !== field.id));
    setAvailableDisplayFields([...availableDisplayFields, field]);
  };

  // Filterable Fields Functions
  const moveToFilterable = (field) => {
    setFilterableFields([...filterableFields, field]);
    setAvailableFilterableFields(availableFilterableFields.filter(f => f.id !== field.id));
  };

  const moveFromFilterable = (field) => {
    setFilterableFields(filterableFields.filter(f => f.id !== field.id));
    setAvailableFilterableFields([...availableFilterableFields, field]);
  };

  // Drag and Drop for Display Fields
  const handleDisplayDragStart = (e, index) => {
    setDraggedDisplayItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDisplayDragOver = (e, index) => {
    e.preventDefault();
    if (draggedDisplayItem !== null && draggedDisplayItem !== index) {
      setDragOverDisplayItem(index);
    }
  };

  const handleDisplayDragLeave = () => {
    setDragOverDisplayItem(null);
  };

  const handleDisplayDrop = (e, index) => {
    e.preventDefault();
    if (draggedDisplayItem === null || draggedDisplayItem === index) {
      setDraggedDisplayItem(null);
      setDragOverDisplayItem(null);
      return;
    }

    const newFields = [...displayFields];
    const draggedField = newFields[draggedDisplayItem];

    newFields.splice(draggedDisplayItem, 1);
    newFields.splice(index, 0, draggedField);

    setDisplayFields(newFields);
    setDraggedDisplayItem(null);
    setDragOverDisplayItem(null);
  };

  const handleDisplayDragEnd = () => {
    setDraggedDisplayItem(null);
    setDragOverDisplayItem(null);
  };

  // Drag and Drop for Filterable Fields
  const handleFilterableDragStart = (e, index) => {
    setDraggedFilterableItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleFilterableDragOver = (e, index) => {
    e.preventDefault();
    if (draggedFilterableItem !== null && draggedFilterableItem !== index) {
      setDragOverFilterableItem(index);
    }
  };

  const handleFilterableDragLeave = () => {
    setDragOverFilterableItem(null);
  };

  const handleFilterableDrop = (e, index) => {
    e.preventDefault();
    if (draggedFilterableItem === null || draggedFilterableItem === index) {
      setDraggedFilterableItem(null);
      setDragOverFilterableItem(null);
      return;
    }

    const newFields = [...filterableFields];
    const draggedField = newFields[draggedFilterableItem];

    newFields.splice(draggedFilterableItem, 1);
    newFields.splice(index, 0, draggedField);

    setFilterableFields(newFields);
    setDraggedFilterableItem(null);
    setDragOverFilterableItem(null);
  };

  const handleFilterableDragEnd = () => {
    setDraggedFilterableItem(null);
    setDragOverFilterableItem(null);
  };

  // Filter available fields by search term
  const filteredAvailableDisplayFields = availableDisplayFields.filter(field =>
    field.label.toLowerCase().includes(displayFieldSearchTerm.toLowerCase()) ||
    field.name.toLowerCase().includes(displayFieldSearchTerm.toLowerCase())
  );

  const filteredAvailableFilterableFields = availableFilterableFields.filter(field =>
    field.label.toLowerCase().includes(filterableFieldSearchTerm.toLowerCase()) ||
    field.name.toLowerCase().includes(filterableFieldSearchTerm.toLowerCase())
  );

  const handleSave = async () => {
    if (!objectName) {
      setError('Please select an object');
      return;
    }

    if (displayFields.length === 0) {
      setError('Please select at least one display field');
      return;
    }

    if (!viewLabel.trim()) {
      setError('View label is required');
      return;
    }

    const slug = searchViewName.trim().toLowerCase();
    if (!slug) {
      setError('Search view name is required');
      return;
    }
    if (!/^[a-z0-9][a-z0-9_-]*$/.test(slug)) {
      setError(
        'Search view name must start with a letter or number and use only lowercase letters, numbers, hyphens, and underscores'
      );
      return;
    }

    setSaving(true);
    setError('');

    try {
      const searchViewApi = new SearchViewApi();
      const data = {
        object_name: objectName,
        view_label: viewLabel.trim(),
        search_view_name: slug,
        display_fields: displayFields.map(f => f.name),
        filterable_fields: filterableFields.map(f => f.name),
        sort_by: sortBy,
        sort_order: sortOrder
      };

      let response;
      if (editMode) {
        response = await searchViewApi.update(searchViewData.id, data);
      } else {
        response = await searchViewApi.create(data);
      }

      if (response?.data?.success) {
        PubSub.publish('SEARCH_VIEW_SAVED', {
          objectName,
          searchView: response.data.data
        });
        onClose();
      } else {
        setError(response?.data?.message || 'Failed to save search view');
      }
    } catch (err) {
      console.error('Error saving search view:', err);
      setError(err.response?.data?.message || 'Failed to save search view');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {editMode ? 'Edit Search View' : 'Configure Search View'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Form>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Label>Object <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={objectName}
                onChange={handleObjectChange}
                disabled={editMode}
                required
              >
                <option value="">Select an object...</option>
                {objects.map(obj => (
                  <option key={obj.name} value={obj.name}>
                    {obj.label} ({obj.name})
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>View label <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={viewLabel}
                onChange={(e) => setViewLabel(e.target.value)}
                placeholder="e.g. Compact list, Inside sales"
                disabled={!objectName}
                required
              />
              <Form.Text className="text-muted">
                Shown in Setup and widget lists.
              </Form.Text>
            </Col>
            <Col md={4}>
              <Form.Label>Search view name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={searchViewName}
                onChange={(e) => setSearchViewName(e.target.value)}
                placeholder="e.g. open_deals"
                disabled={!objectName}
                required
                autoComplete="off"
              />
              <Form.Text className="text-muted">
                Unique URL slug: <code>/search/{objectName || 'object'}/{searchViewName.trim() || 'name'}</code>
                <br />
                Lowercase letters, numbers, hyphens, underscores only.
              </Form.Text>
            </Col>
          </Row>

          {loading && (
            <div className="text-center my-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading fields...</p>
            </div>
          )}

          {objectName && !loading && (
            <>
              {/* Display Fields Section */}
              <Card className="mb-4">
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">
                    <FaEye className="me-2" />
                    Display Fields
                    <span className="text-danger ms-2">*</span>
                  </h5>
                  <small>Fields shown in search results table</small>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Label>Available Fields</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search fields..."
                        value={displayFieldSearchTerm}
                        onChange={(e) => setDisplayFieldSearchTerm(e.target.value)}
                        className="mb-2"
                      />
                      <div
                        style={{
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          height: '300px',
                          overflowY: 'auto',
                          padding: '8px'
                        }}
                      >
                        {filteredAvailableDisplayFields.length === 0 ? (
                          <p className="text-muted text-center mt-3">
                            {displayFieldSearchTerm ? 'No fields match your search' : 'No available fields'}
                          </p>
                        ) : (
                          filteredAvailableDisplayFields.map(field => (
                            <div
                              key={field.id}
                              className="d-flex justify-content-between align-items-center p-2 mb-1 border rounded"
                              style={{ cursor: 'pointer' }}
                            >
                              <span>{field.label}</span>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => moveToDisplay(field)}
                                title="Add to Display Fields"
                              >
                                <FaArrowRight />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </Col>

                    <Col md={6}>
                      <Form.Label>Selected Display Fields</Form.Label>
                      <div
                        style={{
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          height: '300px',
                          overflowY: 'auto',
                          padding: '8px'
                        }}
                      >
                        {displayFields.length === 0 ? (
                          <p className="text-muted text-center mt-3">No display fields selected</p>
                        ) : (
                          displayFields.map((field, index) => (
                            <div
                              key={field.id}
                              draggable
                              onDragStart={(e) => handleDisplayDragStart(e, index)}
                              onDragOver={(e) => handleDisplayDragOver(e, index)}
                              onDragLeave={handleDisplayDragLeave}
                              onDrop={(e) => handleDisplayDrop(e, index)}
                              onDragEnd={handleDisplayDragEnd}
                              className="d-flex justify-content-between align-items-center p-2 mb-1 border rounded"
                              style={{
                                cursor: 'move',
                                backgroundColor: dragOverDisplayItem === index ? '#e3f2fd' : 'white',
                                opacity: draggedDisplayItem === index ? 0.5 : 1
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <FaGripVertical className="me-2 text-muted" />
                                <span>{field.label}</span>
                              </div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => moveFromDisplay(field)}
                                title="Remove"
                              >
                                <FaArrowLeft />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Filterable Fields Section */}
              <Card className="mb-4">
                <Card.Header className="bg-success text-white">
                  <h5 className="mb-0">
                    <FaFilter className="me-2" />
                    Filterable Fields
                  </h5>
                  <small>Fields available for filtering on search page</small>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Label>Available Fields</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search fields..."
                        value={filterableFieldSearchTerm}
                        onChange={(e) => setFilterableFieldSearchTerm(e.target.value)}
                        className="mb-2"
                      />
                      <div
                        style={{
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          height: '300px',
                          overflowY: 'auto',
                          padding: '8px'
                        }}
                      >
                        {filteredAvailableFilterableFields.length === 0 ? (
                          <p className="text-muted text-center mt-3">
                            {filterableFieldSearchTerm ? 'No fields match your search' : 'No available fields'}
                          </p>
                        ) : (
                          filteredAvailableFilterableFields.map(field => (
                            <div
                              key={field.id}
                              className="d-flex justify-content-between align-items-center p-2 mb-1 border rounded"
                              style={{ cursor: 'pointer' }}
                            >
                              <span>{field.label}</span>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => moveToFilterable(field)}
                                title="Add to Filterable Fields"
                              >
                                <FaArrowRight />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </Col>

                    <Col md={6}>
                      <Form.Label>Selected Filterable Fields</Form.Label>
                      <div
                        style={{
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          height: '300px',
                          overflowY: 'auto',
                          padding: '8px'
                        }}
                      >
                        {filterableFields.length === 0 ? (
                          <p className="text-muted text-center mt-3">No filterable fields selected</p>
                        ) : (
                          filterableFields.map((field, index) => (
                            <div
                              key={field.id}
                              draggable
                              onDragStart={(e) => handleFilterableDragStart(e, index)}
                              onDragOver={(e) => handleFilterableDragOver(e, index)}
                              onDragLeave={handleFilterableDragLeave}
                              onDrop={(e) => handleFilterableDrop(e, index)}
                              onDragEnd={handleFilterableDragEnd}
                              className="d-flex justify-content-between align-items-center p-2 mb-1 border rounded"
                              style={{
                                cursor: 'move',
                                backgroundColor: dragOverFilterableItem === index ? '#e8f5e9' : 'white',
                                opacity: draggedFilterableItem === index ? 0.5 : 1
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <FaGripVertical className="me-2 text-muted" />
                                <span>{field.label}</span>
                              </div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => moveFromFilterable(field)}
                                title="Remove"
                              >
                                <FaArrowLeft />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Sort Configuration */}
              <Row className="mt-3">
                <Col md={6}>
                  <Form.Label>Sort By</Form.Label>
                  <Form.Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    {allFields.map(field => (
                      <option key={field.name} value={field.name}>
                        {field.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={6}>
                  <Form.Label>Sort Order</Form.Label>
                  <Form.Select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                    <option value="ASC">Ascending</option>
                    <option value="DESC">Descending</option>
                  </Form.Select>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !objectName || !viewLabel.trim() || !searchViewName.trim() || displayFields.length === 0}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            editMode ? 'Update' : 'Save'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SearchViewDialog;
